import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { clearAuth } from '../auth';
import { ThemeToggle } from '../contexts/ThemeContext';
import { useSettings } from '../contexts/SettingsContext';
import { usePageTitle } from '../hooks/usePageTitle';

type Props = { title?: string; children: React.ReactNode };

const NAV_ITEMS = [
  { to: '/admin-dashboard', label: 'Dashboard' },
  { to: '/admin/users/create', label: 'Create User' },
  { to: '/admin/users/update', label: 'Update User' },
  { to: '/admin/companies', label: 'Companies' },
  { to: '/admin/workflows', label: 'Workflows' },
  { to: '/admin/as2-trading-partners', label: 'AS2' },
  { to: '/admin/settings', label: 'Settings' },
];

// Simple modal component for coming soon popups
function ComingSoonModal({
  isOpen,
  onClose,
  title,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
}) {
  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
      <div className='bg-slate-800 rounded-lg p-6 max-w-md mx-4'>
        <h3 className='text-xl font-semibold text-white mb-4'>{title}</h3>
        <p className='text-slate-300 mb-6'>
          This feature is coming soon! We're working hard to bring you the best experience.
        </p>
        <div className='flex justify-end'>
          <button
            onClick={onClose}
            className='px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors'
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminShell({
  title = 'NGHS Transfer Portal',
  children,
}: Props) {
  console.log('🏗️ AdminShell rendering with title:', title);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { settings, clearLogoUrl } = useSettings();

  // Set page title
  usePageTitle(title);
  const [modalState, setModalState] = useState<{ isOpen: boolean; title: string }>({
    isOpen: false,
    title: '',
  });

  // Debug logging for logo URL (commented out to prevent console spam)
  // console.log('🔍 Debug: AdminShell - Current logo URL:', settings.company.logo);
  // console.log('🔍 Debug: AdminShell - Window config:', (window as any).__APP_CONFIG);

  const showComingSoon = (title: string) => {
    setModalState({ isOpen: true, title });
  };

  const closeModal = () => {
    setModalState({ isOpen: false, title: '' });
  };

  return (
    <div className='min-h-screen text-primary'>
      {/* Top bar */}
      <header className='sticky top-0 z-20 border-b border-primary backdrop-blur bg-overlay'>
        <div className='mx-auto flex max-w-7xl items-center justify-between px-4 py-3'>
          <div className='flex items-center gap-3 font-semibold'>
            {settings.company.logo ? (
              <div className='relative flex-shrink-0'>
                <img
                  src={settings.company.logo}
                  alt='Company Logo'
                  className='h-8 w-16 rounded-lg object-contain logo-image'
                  onError={e => {
                    console.error('Failed to load logo:', settings.company.logo);
                    e.currentTarget.style.display = 'none';
                  }}
                  onLoad={() => {
                    // Logo loaded successfully
                  }}
                />
                {settings.company.logo.includes('ygjn4hiebj') && (
                  <button
                    onClick={() => {
                      // Clearing incorrect logo URL
                      clearLogoUrl();
                    }}
                    className='absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full hover:bg-red-600'
                    title='Clear incorrect logo URL'
                  >
                    ×
                  </button>
                )}
              </div>
            ) : (
              <span className='h-8 w-16 rounded-lg bg-gradient-to-br from-indigo-500 to-cyan-400 logo-placeholder flex-shrink-0' />
            )}
            <span className='text-primary'>NGHS Transfer Portal</span>
          </div>
          <nav className='hidden sm:flex items-center gap-1 text-sm text-slate-300'>
            <ThemeToggle />
            <button
              className='rounded-lg px-3 py-2 hover:bg-white/10'
              onClick={() => showComingSoon('Documentation')}
            >
              Docs
            </button>
            <button
              className='rounded-lg px-3 py-2 hover:bg-white/10'
              onClick={() => showComingSoon('Support')}
            >
              Support
            </button>
            <button
              onClick={() => {
                // Clear all auth tokens
                localStorage.removeItem('authToken');
                localStorage.removeItem('ohit_token');
                sessionStorage.removeItem('ohit_token');

                // Clear auth context
                clearAuth();

                // Navigate to login
                navigate('/login', { replace: true });
              }}
              className='rounded-lg px-3 py-2 hover:bg-white/10 text-red-400 hover:text-red-300'
            >
              Logout
            </button>
          </nav>
        </div>
      </header>

      {/* Body */}
      <div className='mx-auto grid max-w-7xl grid-cols-12 gap-4 px-4 py-6 relative z-10'>
        {/* Sidebar */}
        <aside className='col-span-12 lg:col-span-3'>
          <nav className='rounded-2xl border border-primary p-3 backdrop-blur bg-overlay'>
            <ul className='space-y-1 text-sm'>
              {NAV_ITEMS.map(it => {
                const active = pathname === it.to;
                return (
                  <li key={it.to}>
                    <Link
                      to={it.to}
                      className={[
                        'flex items-center gap-2 rounded-lg px-3 py-2',
                        active ? 'text-white' : 'text-secondary hover:bg-hover',
                      ].join(' ')}
                      style={active ? { backgroundColor: 'var(--color-primary)' } : {}}
                    >
                      <span
                        className='size-1.5 rounded-full'
                        style={{ backgroundColor: 'var(--color-accent)' }}
                      />
                      {it.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>
        </aside>

        {/* Main content */}
        <main className='col-span-12 lg:col-span-9 min-h-[600px] relative z-10'>{children}</main>
      </div>

      {/* Footer removed - using Copyright component in each page instead */}

      {/* Coming Soon Modal */}
      <ComingSoonModal isOpen={modalState.isOpen} onClose={closeModal} title={modalState.title} />
    </div>
  );
}

/*
 * OHIT Watermark - Optimum Healthcare IT Transfer System
 * Copyright © 2025 Optimum Healthcare IT. All rights reserved.
 *
 * This file is part of the OHIT Admin Portal for secure healthcare file transfer.
 * Built with React, TypeScript, and AWS CDK for HIPAA-compliant operations.
 *
 * OHIT: Optimized for Health Information Technology
 * Secure, reliable, and compliant file transfer solutions for healthcare organizations.
 */
